import React, { useMemo, useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { apiPost } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Card, CardContent, CardHeader, CardTitle, CardDescription,
} from "@/components/ui/card";
import { KeyRound, Loader2, CheckCircle2, Circle } from "lucide-react";
import { toast } from "sonner";

const RULES = [
  { id: "len",    label: "At least 8 characters",            test: (p) => p.length >= 8 },
  { id: "lower",  label: "One lowercase letter (a-z)",        test: (p) => /[a-z]/.test(p) },
  { id: "upper",  label: "One uppercase letter (A-Z)",        test: (p) => /[A-Z]/.test(p) },
  { id: "num",    label: "One number (0-9)",                  test: (p) => /\d/.test(p) },
  { id: "sym",    label: "One special character (e.g. !@#$%)", test: (p) => /[^A-Za-z0-9]/.test(p) },
];

const StrengthChecklist = ({ password, match }) => {
  const results = useMemo(() => RULES.map((r) => ({ ...r, ok: r.test(password) })), [password]);
  return (
    <ul className="space-y-1.5" data-testid="reset-strength-checklist">
      {results.map((r) => (
        <li
          key={r.id}
          className={`flex items-center gap-2 text-xs transition-colors duration-150 ${
            r.ok ? "text-[hsl(var(--success))]" : "text-muted-foreground"
          }`}
          data-testid={`rule-${r.id}-${r.ok ? "ok" : "fail"}`}
        >
          {r.ok ? <CheckCircle2 className="h-3.5 w-3.5" /> : <Circle className="h-3.5 w-3.5" />}
          <span>{r.label}</span>
        </li>
      ))}
      <li
        className={`flex items-center gap-2 text-xs transition-colors duration-150 ${
          match ? "text-[hsl(var(--success))]" : "text-muted-foreground"
        }`}
        data-testid={`rule-match-${match ? "ok" : "fail"}`}
      >
        {match ? <CheckCircle2 className="h-3.5 w-3.5" /> : <Circle className="h-3.5 w-3.5" />}
        <span>Both passwords match</span>
      </li>
    </ul>
  );
};

const ResetPasswordPage = () => {
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const token = params.get("token") || "";
  const [pw, setPw] = useState("");
  const [pw2, setPw2] = useState("");
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState(null);
  const [done, setDone] = useState(false);

  const allRulesOk = RULES.every((r) => r.test(pw));
  const match = pw.length > 0 && pw === pw2;
  const canSubmit = allRulesOk && match && !!token && !busy;

  const submit = async (e) => {
    e.preventDefault();
    setErr(null);
    if (!token) { setErr("Missing reset token"); return; }
    if (!allRulesOk) { setErr("Password does not meet all requirements"); return; }
    if (pw !== pw2) { setErr("Passwords do not match"); return; }
    setBusy(true);
    try {
      await apiPost("/auth/reset-password", { token, password: pw });
      setDone(true);
      toast.success("Password updated. All other sessions have been signed out.");
      setTimeout(() => navigate("/login", { replace: true }), 1800);
    } catch (e) {
      setErr(e?.response?.data?.detail || "Reset failed");
    } finally {
      setBusy(false);
    }
  };

  if (done) {
    return (
      <div className="min-h-screen grid place-items-center bg-background px-4" data-testid="reset-success">
        <Card className="w-full max-w-md border bg-card">
          <CardContent className="pt-6 text-center space-y-3">
            <div className="mx-auto inline-flex h-12 w-12 items-center justify-center rounded-xl bg-[hsl(var(--success))]/15 text-[hsl(var(--success))]">
              <CheckCircle2 className="h-7 w-7" />
            </div>
            <h2 className="font-display text-lg font-semibold">Password updated</h2>
            <p className="text-sm text-muted-foreground">
              All other sessions have been signed out for your security.<br />
              Redirecting you to sign in…
            </p>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="min-h-screen grid place-items-center bg-background px-4" data-testid="reset-password-page">
      <Card className="w-full max-w-md border bg-card">
        <CardHeader className="text-center space-y-1">
          <div className="mx-auto mb-2 inline-flex h-12 w-12 items-center justify-center rounded-xl bg-[hsl(var(--primary))]/10 text-[hsl(var(--primary))]">
            <KeyRound className="h-6 w-6" />
          </div>
          <CardTitle className="font-display text-xl">Reset password</CardTitle>
          <CardDescription>Choose a new password</CardDescription>
        </CardHeader>
        <CardContent>
          <form onSubmit={submit} className="space-y-4">
            <div className="space-y-1.5">
              <Label htmlFor="pw">New password</Label>
              <Input
                id="pw"
                type="password"
                value={pw}
                onChange={(e) => setPw(e.target.value)}
                autoComplete="new-password"
                required
                data-testid="reset-pw"
              />
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="pw2">Confirm new password</Label>
              <Input
                id="pw2"
                type="password"
                value={pw2}
                onChange={(e) => setPw2(e.target.value)}
                autoComplete="new-password"
                required
                data-testid="reset-pw2"
              />
            </div>
            <div className="rounded-md border border-border bg-muted/40 px-3 py-2">
              <StrengthChecklist password={pw} match={match} />
            </div>
            {err && (
              <div role="alert" className="rounded-md border border-[hsl(var(--destructive))]/30 bg-[hsl(var(--destructive))]/5 px-3 py-2 text-xs text-[hsl(var(--destructive))]" data-testid="reset-error">
                {err}
              </div>
            )}
            <Button type="submit" disabled={!canSubmit} className="w-full gap-2" data-testid="reset-submit">
              {busy && <Loader2 className="h-4 w-4 animate-spin" />}
              {busy ? "Updating…" : "Update password"}
            </Button>
          </form>
          <p className="mt-4 text-center text-xs text-muted-foreground">
            <Link to="/login" className="text-[hsl(var(--primary))] hover:underline">Back to sign in</Link>
          </p>
        </CardContent>
      </Card>
    </div>
  );
};

export default ResetPasswordPage;
